import { ReactElement, useEffect, useState } from "react";
import LimitlessScalability from "../../assets/images&icons/LimitlessScalability.jpg";
import MoreControlAndManagement from "../../assets/images&icons/MoreControlAndManagement.jpg";
import PowerfulNewDatabase from "../../assets/images&icons/PowerfulNewDatabase.jpg";
import EnhancedSecurity from "../../assets/images&icons/EnhancedSecurity.jpg";
import IntuitiveStreamlinedMenu from "../../assets/images&icons/IntuitiveStreamlinedMenu.png";
import StreamlinedSecureLogin from "../../assets/images&icons/StreamlinedSecureLogin.jpg";
import FasterLoadTimes from "../../assets/images&icons/FasterLoadTimes.jpg";
import TwoFactorAuthentication from "../../assets/images&icons/2fa-example.jpg";
import mywsgpage from "../../assets/images&icons/mywsg.png";

import "./WhatsNew.css";
import Popup from "./Popup/Popup";
import {
  TilesList,
  SecondTitleSection,
  SquaredTilesSection,
  type TileData,
} from "./components";
import WorldBG from "../../assets/images&icons/world-bg.png";

const TILE_DATA: TileData[] = [
  {
    title: "Limitless Scalability",
    text: "Our new serverless architecture adapts to user demand, ensuring optimal performance regardless of workload...",
    image: LimitlessScalability,
    fullText:
      "Our new serverless architecture adapts to user demand, ensuring optimal performance regardless of workload. It effortlessly handles workload spikes without compromising speed and seamlessly integrates future features and enhancements.",
  },
  {
    title: "More Control & Management",
    text: "This means faster issue detection and resolution, seamless operations, and an optimized platform for a smooth user experience.",
    image: MoreControlAndManagement,
    fullText:
      "This means faster issue detection and resolution, seamless operations, and an optimized platform for a smooth user experience.",
  },
  {
    title: "Powerful New Database",
    text: "Transitioned to Cosmos Database, enhancing scalability, speed, and reliability. This upgrade ensures seamless...",
    image: PowerfulNewDatabase,
    fullText:
      "Transitioned to Cosmos Database, enhancing scalability, speed, and reliability. This upgrade ensures seamless, faster data handling with global reach, empowering businesses to grow effortlessly.",
    imageStyle: { mixBlendMode: "darken", objectFit: "contain" },
  },
  {
    title: "Enhanced Security",
    text: "Our infrastructure is now backed and certified by Azure, ensuring greater stability, improved data protection...",
    image: EnhancedSecurity,
    fullText:
      "Our infrastructure is now backed and certified by Azure, ensuring greater stability, improved data protection, compliance with industry standards, and reduced risk of downtime or security vulnerabilities.",
  },
];

const SQUARED_TILE_DATA: TileData[] = [
  {
    title: "Intuitive Design My WSG Page",
    text: "Your space to connect, share, and explore within the WSG community...",
    image: mywsgpage,
    fullText:
      "Your space to connect, share, and explore within the WSG community. Manage your profile, access groups and collaborations, and stay informed—all from your personalized dashboard.",
  },
  {
    title: "Stronger security with Two-Factor Authentication",
    text: "Keep your account safer with Two-Factor Authentication...",
    image: TwoFactorAuthentication,
    fullText:
      "Keep your account safer with Two-Factor Authentication (2FA). It's like adding an extra lock to your door—besides your password, you'll just need a quick code sent to your phone or email. This way, even if someone gets your password, they won't be able to get into your account without you.",
    actionButton: {
      text: "SET UP 2FA",
      rightIcon: ">",
      className: "ml-auto mr-[6%]",
      navigateTo: "/settings",
    },
    imageStyle: { height: "100%", objectFit: "cover", borderRadius: "10px" },
  },
  {
    title: "Intuitive and Streamlined Menu",
    text: "More user-friendly navigation easy to use with fewer clicks...",
    image: IntuitiveStreamlinedMenu,
    fullText:
      "More user-friendly navigation easy to use with fewer clicks.\nFirm Tools to easily view activity with WSG using platform utilities.\nFirm Dashboard for main contacts to view firm activity and status.\nInbox icon to keep up with WSG’s latest news and correspondence.",
  },
  {
    title: "Streamlined and Secure Login",
    text: "Experience a streamlined and secure login process where you can view and reset your password easily and more securely...",
    image: StreamlinedSecureLogin,
    fullText:
      "Experience a streamlined and secure login process where you can view and reset your password easily and more securely. Now, upon accessing the platform, you will be entering a new structure housing all network information and optimizing platform utilities.",
  },
  {
    title: "Faster Load Times & Better Responsiveness",
    text: "Faster loading reflects the new server minimal lag, optimized code, compressed images, and efficient caching...",
    image: FasterLoadTimes,
    imageStyle: { marginBottom: "4%" },
    fullText:
      "Faster loading reflects the new server minimal lag, optimized code, compressed images, and efficient caching. From a user’s perspective, it simply feels effortless—no waiting, no frustration, just seamless access to content. This will be an ongoing enhancement that you should experience throughout your platform experience.",
  },
];

export default function WhatsNew(): ReactElement {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [selectedTileData, setSelectedTileData] = useState<TileData | null>(
    null
  );
  const [deviceType, setDeviceType] = useState("desktop");
  const [showBackground, setShowBackground] = useState(false);

  const openPopupWithTile = (tileData: TileData) => {
    setSelectedTileData(tileData);
    setIsOpen(true);
  };

  const closePopup = () => {
    setIsOpen(false);
    setSelectedTileData(null);
  };

  useEffect(() => {
    const mediaQueryMobile = window.matchMedia("(max-width: 480px)");
    const mediaQueryTablet = window.matchMedia("(max-width: 768px)");
    const mediaQueryShowBg = window.matchMedia("(min-width: 768px)");

    const handleResize = () => {
      if (mediaQueryMobile.matches) {
        setDeviceType("mobile");
      }
      else if (mediaQueryTablet.matches) {
        setDeviceType("tablet");
      }
      else {
        setDeviceType("desktop");
      }
      
      setShowBackground(mediaQueryShowBg.matches);
    };

    handleResize();
    mediaQueryMobile.addEventListener("change", handleResize);
    mediaQueryTablet.addEventListener("change", handleResize);
    mediaQueryShowBg.addEventListener("change", handleResize);

    return () => {
      mediaQueryMobile.removeEventListener("change", handleResize);
      mediaQueryTablet.removeEventListener("change", handleResize);

    };
  }, []);
  return (
    <>
      <div className="bg-[#FFFFFF]">
        <Popup
          isOpen={isOpen}
          onClose={closePopup}
          tileData={selectedTileData}
        />

        <>
          <div
            className="flex flex-row justify-center h-[140px] md:h-[240px] lg:h-[280px] bg-[#bc3323] items-center px-4 sm:px-6 lg:px-8 xl:px-10 py-8 sm:py-12 lg:py-20 xl:py-30"
            style={{
              backgroundImage: showBackground ? `url(${WorldBG})` : 'none',
              backgroundRepeat: "no-repeat",
              backgroundPosition: "right",
            }}
          >
            <h4 className="w-full text-white font-medium sm:text-3xl lg:text-4xl xl:text-5xl ">
              {"Optimized Today. Limitless Tomorrow."}
            </h4>
          </div>
          <TilesList tiles={TILE_DATA} onLearnMore={openPopupWithTile} />
          <SecondTitleSection />
          <SquaredTilesSection
            tiles={SQUARED_TILE_DATA}
            onLearnMore={openPopupWithTile}
          />
        </>
      </div>
    </>
  );
}
